import React, { useCallback, useRef } from "react";
import { Box } from "@mui/material";
import {
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import { dtGreen } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { TrackingSession, TrackingPosition } from "../../../../shared/tracking/trackingSlice";
import ActiveServiceCard from "../ActiveServiceCard";
import LivePulseDot from "../LivePulseDot";

type BottomSheetState = "peek" | "half" | "full";

const BOTTOM_SHEET_HEIGHTS: Record<BottomSheetState, string> = {
  peek: "80px",
  half: "50vh",
  full: "85vh",
};

interface PhoneTrackingLayoutProps {
  activeSessions: TrackingSession[];
  positions: Record<string, TrackingPosition>;
  selectedSessionId: string | null;
  bottomSheet: BottomSheetState;
  onSessionClick: (session: TrackingSession) => void;
  onToggleBottomSheet: () => void;
  onSetBottomSheet: (state: BottomSheetState) => void;
  mapElement: React.ReactNode;
  connectionBanner: React.ReactNode;
}

const PhoneTrackingLayout: React.FC<PhoneTrackingLayoutProps> = ({
  activeSessions,
  positions,
  selectedSessionId,
  bottomSheet,
  onSessionClick,
  onToggleBottomSheet,
  onSetBottomSheet,
  mapElement,
  connectionBanner,
}) => {
  const { translate } = useTranslation(["serviceTracking"]);

  // Touch gesture support for bottom sheet
  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartY.current == null) return;
    const dy = touchStartY.current - e.changedTouches[0].clientY;
    touchStartY.current = null;

    if (dy > 50) {
      onSetBottomSheet(
        bottomSheet === "peek" ? "half" : bottomSheet === "half" ? "full" : "full"
      );
    } else if (dy < -50) {
      onSetBottomSheet(
        bottomSheet === "full" ? "half" : bottomSheet === "half" ? "peek" : "peek"
      );
    }
  }, [bottomSheet, onSetBottomSheet]);

  return (
    <Box
      sx={{
        position: "relative",
        height: "calc(100vh - 120px)",
        backgroundColor: dtGreen.pageBg,
        overflow: "hidden",
      }}
    >
      {/* Map area */}
      <Box sx={{ height: "100%", width: "100%" }}>
        {mapElement}
      </Box>

      {/* Bottom sheet (Wolt-style: peek/half/full) */}
      <Box
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: BOTTOM_SHEET_HEIGHTS[bottomSheet],
          backgroundColor: dtGreen.cardBg,
          borderRadius: "20px 20px 0 0",
          boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
          transition: "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex",
          flexDirection: "column",
          zIndex: 20,
        }}
      >
        {/* Drag handle + header */}
        <Box
          onClick={onToggleBottomSheet}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: "8px",
            pb: "10px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          {/* Drag handle bar */}
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: dtGreen.textMuted,
              mb: "8px",
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontFamily: dtGreen.font,
              fontSize: "14px",
              fontWeight: 600,
              color: dtGreen.textPrimary,
            }}
          >
            <LivePulseDot size={8} color={dtGreen.green500} />
            {activeSessions.length} {translate("serviceTracking:activeServices")}
            {bottomSheet === "peek" ? (
              <ArrowUpIcon sx={{ fontSize: 18, color: dtGreen.textMuted }} />
            ) : (
              <ArrowDownIcon sx={{ fontSize: 18, color: dtGreen.textMuted }} />
            )}
          </Box>
        </Box>

        {/* Connection banner */}
        {bottomSheet !== "peek" && (
          <Box sx={{ px: 2, pb: 1 }}>{connectionBanner}</Box>
        )}

        {/* Scrollable card list */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            pb: 2,
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {activeSessions.map((session) => (
            <ActiveServiceCard
              key={session.id}
              session={session}
              position={positions[session.id] || null}
              isSelected={session.id === selectedSessionId}
              onClick={onSessionClick}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(PhoneTrackingLayout);
